import {
  ChangeDetectionStrategy,
  Component,
  computed, inject,
  input,
  output,
} from '@angular/core';

import { IconComponent } from '../../design-system/components/icon/icon.component';

import {
  AnimalEventStatus,
  resolveAnimalEventStatus,
} from '../../utils/animal-event-status.util';
import { SharedPetUtilsService } from "../../services/shared-pet-utils.service";
import { DatesHandlerService } from "../../services/dates-handler.service";
import { NewLoadingComponent } from "../../new-loading/new-loading.component";
import { AnimalHistory, AnimalHistoryDisplayItem } from "./survey-history.model";

@Component({
  selector: 'lib-survey-history',
  standalone: true,
  imports: [IconComponent, NewLoadingComponent],
  templateUrl: './survey-history.component.html',
  styleUrl: './survey-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyHistoryComponent {
  private sharedPetUtilsService = inject(SharedPetUtilsService);
  private datesHandlerService = inject(DatesHandlerService);

  readonly petsHistory = input<AnimalHistory[] | null>(null);

  readonly errorMessage =
    input<string | null>(null);

  /*
   * Staff-only behaviors are disabled by default.
   * Shelter therefore retains plain animal names
   * and no impersonation controls.
   */
  readonly allowAnimalNavigation =
    input(false);

  readonly allowImpersonation =
    input(false);

  readonly viewAnimal =
    output<string>();

  readonly impersonate =
    output<string>();

  readonly petsFormatted =
    computed<AnimalHistoryDisplayItem[]>(
      () => {
        const pets = this.petsHistory();
        if (pets && pets.length > 0) {
          return this.formatPets(pets);
        } else {
          return [];
        }
      }
    );

  onViewAnimal(
    petszelAnimalId: string,
  ): void {
    const normalizedId =
      petszelAnimalId.trim();

    if (
      !this.allowAnimalNavigation() ||
      !normalizedId
    ) {
      return;
    }

    this.viewAnimal.emit(
      normalizedId,
    );
  }

  onImpersonate(
    petszelAnimalId: string,
  ): void {
    const normalizedId =
      petszelAnimalId.trim();

    if (
      !this.allowImpersonation() ||
      !normalizedId
    ) {
      return;
    }

    this.impersonate.emit(
      normalizedId,
    );
  }

  private formatPets(pets: AnimalHistory[]): AnimalHistoryDisplayItem[] {
    return pets.map((entry) => {
      const status =
        resolveAnimalEventStatus(
          entry.status,
        );

      const latestEvent = entry.events[entry.events.length - 1];
      const latestEventDateFormatted = latestEvent.eventDate ?
        this.datesHandlerService.formatUTCToCST(latestEvent.eventDate) : '';

      return {
        id: entry.id.trim(),
        animalName:
          entry.animalName
            ?.trim() ||
          'Unnamed pet',

        animalDetails:
          this.formatAnimalDetails(
            entry,
          ),

        status:
        status.label,

        statusClass:
          this.getStatusClass(
            status.status,
          ),

        iconName:
          this.getAnimalIcon(
            entry.animalType,
          ),

        iconClass:
          this.getAnimalIconClass(
            entry.animalSex,
          ),

        canImpersonate: entry.canImpersonate,
        petszelAnimalId: entry.petszelAnimalId,
        latestEvent: latestEvent.event,
        latestSubEvent: latestEvent.subEvent,
        latestEventDate: latestEventDateFormatted
      };
    });
  }

  private formatAnimalDetails(
    entry: AnimalHistory,
  ): string {
    const age =
      this.sharedPetUtilsService.calculateAge(entry.dateOfBirth);

    const breed =
      entry.animalBreed?.trim();

    return [age, breed]
      .filter(Boolean)
      .join(', ');
  }

  private getAnimalIcon(
    animalType: string,
  ): string {
    const normalizedType =
      animalType
        ?.trim()
        .toLowerCase();

    return normalizedType === 'cat'
      ? 'catAvatarIcon'
      : 'dogAvatarIcon';
  }

  private getAnimalIconClass(
    animalSex: string,
  ): string {
    return animalSex
      ?.trim()
      .toLowerCase() ===
      'female'
      ? 'survey-history__pet-icon--female'
      : 'survey-history__pet-icon--male';
  }

  private getStatusClass(
    status: AnimalEventStatus,
  ): string {
    return `survey-history__status--${status}`;
  }
}
